import { useParams } from "@remix-run/react";
import clsx from "clsx";
import { Fragment, useState } from "react";
import { ForcedAspectRatio, ContentfulCenteredImage } from "../shared";
import Experts from "./Experts";
import TopicComponent from "./Topic";
import { getPrettyDate } from "../utils/getPrettyDate";
import { InsightGetters } from "~/@types";
import NavOrExternalLink from "../shared/NavOrExternalLink";
import {
  InsightComponentData,
  useComponentData,
} from "~/contexts/ComponentDataProvider";
import { truncateString } from "../utils/helper";

type ColumnCardProps = {
  insights: InsightGetters[];
  className?: string;
  onClick?: (insight: InsightGetters) => void;
};

export default function InsightCardWithImage({
  insights,
  className,
  onClick,
}: ColumnCardProps) {
  const { topicSlug } = useParams();
  const [hoveredArticle, setHoveredArticle] = useState<string | null>(null);
  const { insight } = useComponentData() as InsightComponentData;
  const isInsight = !!insight;

  // This component is typically only rendered on list pages
  if (!insights || insights.length === 0) return null;

  const getCardStuff = (insight: InsightGetters, ix: number) => (
    <>
      {/* this is the image displayed */}
      {insight.getImageFields() ? (
        <ContentfulCenteredImage
          image={insight.getImageFields()!}
          width={900}
          className={clsx([
            "h-full",
            "w-full",
            "group-hover:scale-105",
            "transition-transform",
            "duration-1000",
            "ease-in-out",
            "-z-10",
            `${hoveredArticle === insight.getId() ? "scale-105" : ""}`,
          ])}
        />
      ) : (
        <div
          className="flex h-full w-full items-center justify-center text-white"
          style={{
            backgroundColor: insight.getTopic()?.color ?? "#F5F5F5",
          }}
        >
          <h4>{insight.getTopic()?.title}</h4>
        </div>
      )}
    </>
  );

  return (
    <section className={clsx("insight-section lg:py-10", className)}>
      {/* Mobile view */}
      <div className="lg:hidden">
        {insights.map((insight) => {
          if (insight)
            return (
              <Fragment key={insight.getId()}>
                <NavOrExternalLink
                  onClick={() => onClick?.(insight)}
                  to={`/insights/${
                    insight.getTopic()?.slug
                  }/${insight.getSlug()}`}
                  prefetch="intent"
                  className="tbi-grid target-child my-8"
                  data-content-type="columnCard"
                  data-module-title={insight.getSlug()}
                >
                  <div className="global-grid last-list-item col-span-full border-b-[1px] border-b-black pb-6">
                    <ForcedAspectRatio
                      className="col-span-1 h-[119px] w-[90px]"
                      ratio={[90, 119]}
                    >
                      {insight.getImageFields() ? (
                        <ContentfulCenteredImage
                          image={insight.getImageFields()!}
                          className="h-full w-full"
                          width={500}
                          transformations="&w=500"
                        />
                      ) : (
                        <div
                          className="flex h-full w-full items-center justify-center text-white"
                          style={{
                            backgroundColor:
                              insight.getTopic()?.color ?? "#F5F5F5",
                          }}
                        >
                          <h4 className="text-center text-sm">
                            {insight.getTopic()?.title}
                          </h4>
                        </div>
                      )}
                    </ForcedAspectRatio>

                    <div className="h-inherit col-span-3 flex flex-col pl-4">
                      {topicSlug && !isInsight ? (
                        <Experts experts={insight.getExperts()!} />
                      ) : (
                        <>
                          {insight.getTopic() ? (
                            <TopicComponent
                              topic={insight.getTopic()!}
                              isLink={false}
                              padding={false}
                            />
                          ) : null}
                        </>
                      )}

                      <h5 className="line-clamp-4 mt-3">
                        {insight.getTitle()}
                      </h5>
                      <p className="meta mt-3 uppercase text-tertiary_text">
                        {insight.getType()} | {getPrettyDate(insight.getDate())}
                      </p>
                    </div>
                    {insight.getDescription!() && (
                      <p className="body-small col-span-full mt-4">
                        {truncateString(insight.getDescription!(), 160)}
                      </p>
                    )}
                  </div>
                </NavOrExternalLink>
              </Fragment>
            );
          return null;
        })}
      </div>

      {/* Desktop view */}
      <div className="tbi-grid divCustom lgmax:hidden">
        {insights.map((insight, ix) => {
          if (insight)
            return (
              <Fragment key={insight.getId()}>
                {insight ? (
                  <div data-type="insightCard" className="col-span-4">
                    <NavOrExternalLink
                      onClick={() => onClick?.(insight)}
                      to={`/insights/${
                        insight.getTopic()?.slug
                      }/${insight.getSlug()}`}
                      prefetch={"intent"}
                      className="group block h-full w-full"
                    >
                      <ForcedAspectRatio
                        className="relative w-full overflow-hidden"
                        ratio={[16, 9]} // 411, 234
                      >
                        {getCardStuff(insight, ix)}
                      </ForcedAspectRatio>
                      <div className="mt-9 mr-12">
                        {/* Only show the experts when it's on a topic page. 
                      The existence of a topicSlug indicates we're on a topic page */}
                        {topicSlug && !isInsight ? (
                          <Experts experts={insight.getExperts()!} />
                        ) : (
                          <>
                            {insight.getTopic() ? (
                              <TopicComponent
                                topic={insight.getTopic()!}
                                isLink={false}
                                padding={false}
                              />
                            ) : null}
                          </>
                        )}
                        <h5
                          className="line-clamp-4 mt-4 lg:text-32"
                          onMouseEnter={() =>
                            setHoveredArticle(insight.getId())
                          }
                          onMouseLeave={() => setHoveredArticle(null)}
                        >
                          {insight.getTitle()}
                        </h5>
                        <p className="meta mt-4 uppercase text-tertiary_text">
                          {insight.getType()} |{" "}
                          {getPrettyDate(insight.getDate())}
                        </p>
                        <p className="body-small mt-4">
                          {truncateString(insight.getDescription!(), 160)}
                        </p>
                      </div>
                    </NavOrExternalLink>
                  </div>
                ) : null}
              </Fragment>
            );
          return null;
        })}
      </div>
    </section>
  );
}
